/**
 * DATE PRESETS — the server's range enum, plus the three the brief asks for that
 * the server has no name for.
 *
 * `meta.ranges` ships eight named ranges and the server resolves each one on the
 * clock of whoever asks (R-T12: the device's for an owner or a manager, the
 * building's for a concierge) — which is why those are always preferred.
 *
 * "This quarter", "Year to date" and a hand-picked range are none of them. They
 * are ordinary windows of whole days and they travel as DAY KEYS
 * (`from_day` / `to_day`, both inclusive — R-T5): the server turns a key into
 * instants on the reader's clock, one window for every building. Nothing here
 * turns a picked day into an instant any more. It used to — browser-local
 * midnight, then `toISOString()` — and everything downstream that sliced that
 * string back to a date was a day early east of Greenwich.
 *
 * "Today" is passed IN, as a day key on the reader's clock, so this file never
 * consults the browser's clock for itself.
 */
import { CUSTOM_RANGE } from '../state/filter-model';
import {
  addMonths,
  isDayKey,
  quarterStartOf,
  yearStartOf,
} from '../../../../../function-utils/building-time';

/** A window of whole days, both ends inclusive, as day keys. */
export interface DayWindow {
  from: string;
  to: string;
}

export interface DatePreset {
  id: string;
  label: string;
  /** A server range name, or null for a window of day keys. */
  range: string | null;
  /** Computed from "today" when pressed — never captured at module load. */
  window?: (today: string) => DayWindow;
}

const quarterWindow = (today: string): DayWindow => ({
  from: quarterStartOf(today),
  to: today,
});

const yearToDateWindow = (today: string): DayWindow => ({
  from: yearStartOf(today),
  to: today,
});

/** The same period, one year earlier. `BREAKDOWN_COMPARE` is
 *  ['none','previous_period'] only, so "same period last year" is served as a
 *  SECOND window rather than as a compare mode the server knows. Calendar
 *  arithmetic on keys: 29 February maps to 28 February. */
export const sameWindowLastYear = (from: string, to: string): DayWindow | null =>
  isDayKey(from) && isDayKey(to)
    ? { from: addMonths(from, -12), to: addMonths(to, -12) }
    : null;

/** Labels for the server's own range names. `humanise` would give "Last 30
 *  days" for free, but three of them read badly derived and one is ambiguous. */
const RANGE_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: '7 days',
  last_30_days: '30 days',
  this_month: 'This month',
  last_month: 'Last month',
  this_shift: 'This shift',
  last_shift: 'Last shift',
};

export const EXTRA_PRESETS: DatePreset[] = [
  { id: 'quarter', label: 'Quarter', range: null, window: quarterWindow },
  { id: 'ytd', label: 'Year to date', range: null, window: yearToDateWindow },
];

export const presetsFor = (ranges: string[]): DatePreset[] => [
  ...ranges.map(range => ({
    id: range,
    label: RANGE_LABELS[range] || range.replace(/_/g, ' '),
    range,
  })),
  ...EXTRA_PRESETS,
  { id: CUSTOM_RANGE, label: 'Custom…', range: null },
];

/** Which preset a filter is currently on, for the pressed state. */
export const activePresetId = (
  range: string,
  from: string | null,
  to: string | null,
  today: string
): string => {
  if (range !== CUSTOM_RANGE) return range;
  for (const preset of EXTRA_PRESETS) {
    const w = preset.window?.(today);
    if (w && w.from === from && w.to === to) return preset.id;
  }

  return CUSTOM_RANGE;
};

/**
 * A native date input's value IS a day key, and so is what the filter stores:
 * the two are handed to each other as they are. These two exist only to refuse
 * anything that is not a real calendar day (a half-typed value, a stale link).
 */
export const toDateInput = (key: string | null): string => (isDayKey(key) ? key : '');

export const fromDateInput = (value: string): string | null =>
  isDayKey(value) ? value : null;
